import { BACKEND_URL } from './config';
import { getAccessToken } from './session';
import { drainSseStream, assertSseResponse } from './sse';

export interface ParcoursChoice {
  id: string;
  letter: string;
  label: string;
}

export interface ParcoursQuestion {
  index: number;
  total: number;
  title: string;
  prompt_text: string;
  choices: ParcoursChoice[];
}

export interface InteractiveDone {
  reply: string;
  current: ParcoursQuestion | null;
  completed: boolean;
  advanced: boolean;
  attempts: number;
  recap: string | null;
}

export interface InteractiveStreamCallbacks {
  onDelta: (text: string) => void;
  /** L'énoncé suivant, posé d'un bloc : une bulle à part, pas la suite de la précédente. */
  onQuestion: (text: string) => void;
  /** Le bilan de fin, émis avant le figeage du parcours. */
  onRecap: (text: string) => void;
  onDone: (done: InteractiveDone) => void;
  onError: (message: string) => void;
}

/** Un tour de parcours interactif, streamé (SSE). Naïa guide en direct, puis
 *  `done` porte le nouvel état (question suivante / terminé). Un `message` vide
 *  ouvre le parcours : Naïa salue et pose la première question. */
export async function streamInteractiveTurn(
  studentId: string,
  assignmentId: string,
  message: string,
  cb: InteractiveStreamCallbacks,
  signal?: AbortSignal,
): Promise<void> {
  const token = await getAccessToken();
  const res = await fetch(`${BACKEND_URL}/interactive/turn`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Accept: 'text/event-stream',
      Authorization: `Bearer ${token ?? ''}`,
    },
    body: JSON.stringify({ studentId, assignmentId, message }),
    signal,
  });
  await assertSseResponse(res, 'interactive');
  await drainSseStream(res.body!, ({ event, data }) => {
    if (event === 'delta') cb.onDelta((JSON.parse(data) as { text?: string }).text ?? '');
    else if (event === 'question') cb.onQuestion((JSON.parse(data) as { text?: string }).text ?? '');
    else if (event === 'recap') cb.onRecap((JSON.parse(data) as { text?: string }).text ?? '');
    else if (event === 'done') cb.onDone(JSON.parse(data) as InteractiveDone);
    else if (event === 'error') cb.onError((JSON.parse(data) as { message?: string }).message ?? 'Erreur');
  });
}
